import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Dialog } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { fetchFundingHistoryApi, fetchFundingHistoryDetailApi } from "./aiFundingApi";

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatAmount(value) {
  if (value == null || Number.isNaN(Number(value))) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value));
}

function formatRange(filters = {}) {
  const from = formatAmount(filters.amount_min);
  const to = formatAmount(filters.amount_max);
  if (from && to) return `${from} - ${to}`;
  if (from) return `${from}+`;
  if (to) return `<= ${to}`;
  return "";
}

export default function AIFundingHistoryModal({ isOpen, onClose, onLoadAnalysis }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [loadingRequestId, setLoadingRequestId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setStatus("loading");
    setError("");

    fetchFundingHistoryApi({ limit: 30 })
      .then((result) => {
        if (cancelled) return;
        if (!result.ok) {
          setStatus("error");
          setError(result.message);
          return;
        }
        setItems(result.items);
        setTotal(result.total);
        setStatus("success");
      })
      .catch((nextError) => {
        if (cancelled) return;
        setStatus("error");
        setError(nextError?.message || t("app:AiFunding.historyLoadFailed"));
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, t]);

  const handleLoad = async (requestId) => {
    setLoadingRequestId(requestId);
    setError("");
    try {
      const result = await fetchFundingHistoryDetailApi(requestId);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      onLoadAnalysis?.(result.analysis);
      onClose?.();
    } catch (nextError) {
      setError(nextError?.message || t("app:AiFunding.historyLoadFailed"));
    } finally {
      setLoadingRequestId(null);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t("app:AiFunding.historyTitle")}
      size="3xl"
      stickyFooter
      bodyClassName="pb-3"
      footerActions={(
        <Button type="button" variant="outline" size="sm" onClick={onClose} iconLeft={<X />}>
          {t("app:AiFunding.historyClose")}
        </Button>
      )}
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-2 rounded-lg border border-stone-200 bg-stone-50/70 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-medium text-stone-950">{t("app:AiFunding.historySubtitle")}</div>
            <div className="mt-1 text-xs text-stone-500">
              {t("app:AiFunding.historyCount", { count: total })}
            </div>
          </div>
          <Search className="hidden h-4 w-4 text-stone-400 sm:block" />
        </div>

        {error ? (
          <div className="rounded-lg border border-red-100 bg-red-50/70 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}

        {status === "loading" ? (
          <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-5 text-sm text-stone-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("app:AiFunding.historyLoading")}
          </div>
        ) : null}

        {status === "success" && items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-stone-300 bg-white px-4 py-5 text-sm text-stone-600">
            {t("app:AiFunding.historyEmpty")}
          </div>
        ) : null}

        {status === "success" && items.length > 0 ? (
          <div className="space-y-2">
            {items.map((item) => {
              const amountRange = formatRange(item.filters);
              const isLoading = loadingRequestId === item.requestId;
              return (
                <button
                  key={item.requestId}
                  type="button"
                  onClick={() => handleLoad(item.requestId)}
                  disabled={Boolean(loadingRequestId)}
                  className="flex w-full cursor-pointer flex-col gap-3 rounded-lg border border-stone-200 bg-white p-3 text-left transition-colors hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-row sm:items-start sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500">
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="h-3.5 w-3.5" />
                        {formatDate(item.createdAt)}
                      </span>
                      {item.primaryIndustry ? (
                        <span className="rounded-full bg-stone-100 px-2 py-0.5 font-medium text-stone-700">
                          {item.primaryIndustry}
                        </span>
                      ) : null}
                      {amountRange ? <span>{amountRange}</span> : null}
                    </div>
                    <div className="mt-2 text-sm font-medium text-stone-950">
                      {item.headline || t("app:AiFunding.historyUntitled")}
                    </div>
                    <div className="mt-1 line-clamp-2 text-xs leading-5 text-stone-600">
                      {item.descriptionPreview}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-xs font-medium text-stone-500">
                    <span>{t("app:AiFunding.historyDeals", { count: item.marketTotalDeals || 0 })}</span>
                    <span>{t("app:AiFunding.historyInvestors", { count: item.investorCount || 0 })}</span>
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-stone-500" /> : null}
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
